import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Radio, Spin } from 'antd';
import PropTypes from 'prop-types';
import {
  fetchTherapyTerminationPreview,
  terminateTherapy,
} from '../../api/therapiesApi';
import {
  getOccurrenceSecondaryStatusLabel,
  getOccurrenceStatusLabel,
} from '../../utils/therapyOccurrencePresentation';
import StatusFlag from '../../../../components/elements/StatusFlag';
import { getOccurrenceStatusTone } from '../../../../shared/utils/statusTones';

const getPreviewFromResponse = (data) => data?.data || data || null;

const getActivityIds = (activities) =>
  (Array.isArray(activities) ? activities : [])
    .map((activity) => activity?._id)
    .filter(Boolean);

const activityListStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const activityRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 0',
  borderBottom: '1px solid #f0f0f0',
};

const activityStatusStyle = {
  marginTop: 4,
  color: '#667085',
  fontSize: 13,
};

const TerminateTherapyModal = ({
  isOpen,
  onClose,
  therapy,
  onTerminated,
  onLoadingChange,
}) => {
  const [preview, setPreview] = useState(null);
  const [keepActivityIds, setKeepActivityIds] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [error, setError] = useState('');
  const [confirmReservedVisible, setConfirmReservedVisible] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadPreview = async () => {
      if (!therapy?._id) {
        setError('Terapija nije izabrana.');
        return;
      }

      setLoadingPreview(true);
      onLoadingChange?.(true);
      setError('');
      setPreview(null);
      setKeepActivityIds([]);

      const { data, ok, message } = await fetchTherapyTerminationPreview(
        therapy._id
      );

      if (cancelled) return;

      const nextPreview = getPreviewFromResponse(data);

      if (!ok || !nextPreview) {
        setError(message || 'Doslo je do greske pri dobavljanju pregleda.');
        setLoadingPreview(false);
        onLoadingChange?.(false);
        return;
      }

      setPreview(nextPreview);
      setKeepActivityIds(nextPreview.recommendedKeepActivityIds || []);
      setLoadingPreview(false);
      onLoadingChange?.(false);
    };

    loadPreview();

    return () => {
      cancelled = true;
      onLoadingChange?.(false);
    };
  }, [isOpen, onLoadingChange, therapy?._id]);

  const activities = useMemo(
    () => (Array.isArray(preview?.activities) ? preview.activities : []),
    [preview?.activities]
  );

  const allActivityIds = useMemo(() => getActivityIds(activities), [activities]);

  const deleteActivityIds = useMemo(
    () => allActivityIds.filter((id) => !keepActivityIds.includes(id)),
    [allActivityIds, keepActivityIds]
  );

  // Aktivnosti koje su i nedostupne i rezervisane (trazi se dodatna potvrda).
  const reservedUnavailableSequenceNumbers = useMemo(
    () =>
      activities
        .filter(
          (activity) =>
            activity?.status === 'unavailable' &&
            activity?.secondaryStatus === 'reserved'
        )
        .map((activity) => activity?.sequenceNumber)
        .filter((sequenceNumber) => sequenceNumber != null)
        .sort((a, b) => Number(a) - Number(b)),
    [activities]
  );

  // Rezervacija unapred moze pripadati trenutnoj terapiji ili cekati primenu
  // nakon prelaska sa prethodne terapije.
  const pendingReservationActivation = !!therapy?.reservationActivation;
  const advanceReservationCount = useMemo(() => {
    const reservation =
      therapy?.reservation || therapy?.reservationActivation;
    const count = Number(reservation?.count);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }, [therapy?.reservation, therapy?.reservationActivation]);

  const hasReservedUnavailable = reservedUnavailableSequenceNumbers.length > 0;
  const hasAdvanceReservation = advanceReservationCount > 0;
  const needsReservationConfirm = hasReservedUnavailable || hasAdvanceReservation;

  // Tekst potvrde za tri kombinacije: rezervacija u trenutnoj terapiji,
  // rezervacija unapred, ili obe.
  const reservationConfirmMessage = useMemo(() => {
    const currentPart = hasReservedUnavailable
      ? reservedUnavailableSequenceNumbers.length === 1
        ? `Mesečna aktivnost pod brojem ${reservedUnavailableSequenceNumbers[0]} je nedostupna, a rezervisana.`
        : `Mesečne aktivnosti pod brojevima ${reservedUnavailableSequenceNumbers.join(
            ', '
          )} su nedostupne, a rezervisane.`
      : '';

    const advanceReservationMessage = pendingReservationActivation
      ? `rezervacija unapred za ${advanceReservationCount} terapija koja treba da se primeni`
      : `rezervacija unapred za ${advanceReservationCount} terapija`;
    const advancePart = hasAdvanceReservation
      ? currentPart
        ? `Takođe, postoji i ${advanceReservationMessage}.`
        : `Postoji ${advanceReservationMessage}.`
      : '';

    return [
      currentPart,
      advancePart,
      'Da li ste sigurni da želite da prekinete terapiju?',
    ]
      .filter(Boolean)
      .join(' ');
  }, [
    advanceReservationCount,
    hasAdvanceReservation,
    hasReservedUnavailable,
    pendingReservationActivation,
    reservedUnavailableSequenceNumbers,
  ]);

  const handleActivityActionChange = (activityId, action) => {
    setKeepActivityIds((prev) => {
      if (action === 'keep') {
        return prev.includes(activityId) ? prev : [...prev, activityId];
      }

      return prev.filter((id) => id !== activityId);
    });
  };

  const handleCancel = () => {
    setError('');
    setPreview(null);
    setKeepActivityIds([]);
    setConfirmReservedVisible(false);
    onLoadingChange?.(false);
    onClose?.();
  };

  const submitTerminate = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }

    setTerminating(true);
    onLoadingChange?.(true);
    setError('');

    try {
      const { data, ok, message } = await terminateTherapy(therapy._id, {
        keepActivityIds,
        deleteActivityIds,
      });

      if (!ok) {
        setError(message);
        return;
      }

      await onTerminated?.(data);
      handleCancel();
    } finally {
      setTerminating(false);
      onLoadingChange?.(false);
    }
  }, [
    deleteActivityIds,
    keepActivityIds,
    onLoadingChange,
    onTerminated,
    therapy?._id,
  ]);

  const handleTerminate = useCallback(() => {
    // Dodatna potvrda ako postoji rezervisana nedostupna aktivnost u trenutnoj
    // terapiji i/ili rezervacija unapred za buduce terapije.
    if (needsReservationConfirm) {
      setConfirmReservedVisible(true);
      return;
    }

    submitTerminate();
  }, [needsReservationConfirm, submitTerminate]);

  const handleConfirmReservedTerminate = useCallback(() => {
    setConfirmReservedVisible(false);
    submitTerminate();
  }, [submitTerminate]);

  const handleConfirmReservedCancel = useCallback(() => {
    setConfirmReservedVisible(false);
  }, []);

  const busy = loadingPreview || terminating;

  return (
    <Modal
      className="create_user_modal"
      title="Prekini terapiju"
      open={isOpen}
      onCancel={busy ? undefined : handleCancel}
      closable={!busy}
      mask={{ closable: !busy }}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={busy}>
          Odustani
        </Button>,
        <Button
          key="terminate"
          type="primary"
          onClick={handleTerminate}
          disabled={busy || !activities.length}
          danger
        >
          {terminating ? 'Prekid terapije...' : 'Prekini terapiju'}
        </Button>,
      ]}
      width={760}
    >
      <Spin spinning={busy}>
        <Alert
          type="warning"
          showIcon
          title="Izaberite koje mesečne aktivnosti ostaju u istoriji, a koje se brišu."
          style={{ marginBottom: 16 }}
        />
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={activityListStyle}>
          {activities.map((activity) => {
            const activityId = activity?._id;
            const selectedAction = keepActivityIds.includes(activityId)
              ? 'keep'
              : 'delete';
            const secondaryLabel = getOccurrenceSecondaryStatusLabel(
              activity?.secondaryStatus
            );

            return (
              <div
                key={activityId}
                style={activityRowStyle}
              >
                <div>
                  <strong>Mesec {activity?.sequenceNumber || '-'}</strong>
                  <div style={activityStatusStyle}>
                    <StatusFlag tone={getOccurrenceStatusTone(activity?.status)}>
                      {getOccurrenceStatusLabel(activity?.status)}
                      {secondaryLabel ? ` (${secondaryLabel})` : ''}
                    </StatusFlag>
                  </div>
                </div>
                <Radio.Group
                  value={selectedAction}
                  onChange={(event) =>
                    handleActivityActionChange(activityId, event.target.value)
                  }
                >
                  <Radio value="keep">Sačuvaj</Radio>
                  <Radio value="delete">Obriši</Radio>
                </Radio.Group>
              </div>
            );
          })}
        </div>
      </Spin>
      <Modal
        className="therapy_confirm_modal"
        title="Rezervisana terapija"
        open={confirmReservedVisible}
        onOk={handleConfirmReservedTerminate}
        onCancel={handleConfirmReservedCancel}
        okText="Prekini"
        cancelText="Odustani"
        okButtonProps={{ danger: true }}
        centered
        destroyOnHidden
      >
        {reservationConfirmMessage}
      </Modal>
    </Modal>
  );
};

TerminateTherapyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  onTerminated: PropTypes.func,
  onLoadingChange: PropTypes.func,
};

export default TerminateTherapyModal;
